<template>
  <section class="high-frequency-results-card rounded-l p-200" :style="theme">
    <LcHighFrequencyBallBackground
      class="background rounded-l"
      :ball-config="backgroundBallConfig"
    />

    <LcCard class="rounded-m bg-secondary-secondary" body-class="pb-none">
      <template #header>
        <div class="flex items-center justify-between">
          <div class="flex flex-wrap items-center">
            <h2 class="paragraph-regular mr-100">
              {{ $t('highfrequencygame.badge.game.label', 1) }}
              <Transition name="fade-game-number">
                <strong v-if="headerGameNumber" class="paragraph-bold">
                  <HighFrequencyGameResultsGameNumber :game-number="headerGameNumber" />
                </strong>
              </Transition>
            </h2>
            <div
              class="latest-badge caption-bold rounded-s bg-accent-win-win p-050"
              :class="{ visible: isLatest }"
            >
              {{ $t('highfrequencygame.hub.results.latest.label') }}
            </div>
          </div>
          <div class="arrows flex gap-100">
            <LcButton
              :aria-label="$t('common.carousel.button.prev')"
              :disabled="!canNavigateOlder"
              variant="filled"
              size="small"
              color="primary"
              class="p-050"
              icon="chevron-left"
              @click="carouselRef?.prev()"
            />
            <LcButton
              :aria-label="$t('common.carousel.button.next')"
              :disabled="!canNavigateNewer"
              variant="filled"
              size="small"
              color="primary"
              class="p-050"
              icon="chevron-right"
              @click="carouselRef?.next()"
            />
          </div>
        </div>
      </template>

      <div ref="resultsViewport" class="inner-card-wrap mb-200" :style="innerCardStyle">
        <button
          v-if="hasNewGame"
          class="new-game-badge caption-bold rounded-s bg-accent-win-win"
          @click="(searchFormRef?.internal?.resetField('gameNumber'), emit('new-game'))"
        >
          {{ $t('highfrequencygame.hub.results.newgame.label') }}
          <LcIcon class="new-game-badge-icon" name="arrowhead-right" size="small" />
        </button>

        <LcCard
          v-if="isSearching"
          class="flex-1 flex"
          body-class="flex h-full justify-center items-center p-100"
        >
          <Illustration
            class="pb-200 pt-200"
            :alt="$t('highfrequencygame.hub.results.illustration.searching.alt')"
            name="keno-hub-results-search"
          />
        </LcCard>

        <LcCard
          v-else-if="isGameNotFound"
          class="flex-1 flex"
          body-class="flex h-full justify-center items-center p-100"
        >
          <div class="flex items-center">
            <div>
              <h3 class="paragraph-bold">
                {{ $t('highfrequencygame.hub.results.notfound.headline') }}
              </h3>
              <p
                class="game-not-found-description paragraph-regular mt-100 text-secondary-secondary"
              >
                {{ $t('highfrequencygame.hub.results.notfound.description') }}
              </p>
            </div>
            <Illustration
              class="game-not-found-illustration"
              :alt="$t('highfrequencygame.hub.results.notfound.headline')"
              name="keno-hub-results-not-found"
            />
          </div>
        </LcCard>

        <LcCarousel
          v-else-if="draws.length"
          :key="carouselSlides.length > 1 ? 'all-draws' : 'current-draw'"
          ref="carouselRef"
          class="results-carousel"
          :adaptive-height="true"
          :default-index="carouselStartIndex"
          :key-extractor="(slide) => slide.drawNumber"
          :loop="false"
          :slides="carouselSlides"
          @changed="onCarouselChanged"
          @ready="onCarouselReady"
        >
          <template #slide="{ attributes, slide }">
            <div v-bind="attributes" class="slide" :class="{ activated: isSlideActivated(slide) }">
              <LcCard body-class="flex justify-center p-100">
                <div class="w-full">
                  <HighFrequencyGameAnimatedNumbers
                    :key="`numbers-${slideKey(slide)}`"
                    class="animated-numbers mb-100"
                    :revealed-numbers="slide.drawnNumbers"
                    animate="slide-up"
                    :no-animation="isInitialReveal"
                    :fields="fieldsForSlide(slide)"
                    :slide-up-stagger-ms="BALL_STAGGER_MS"
                    :slide-up-duration-ms="BALL_ANIMATION_MS"
                  />

                  <HighFrequencyGameBonus
                    v-if="slide.bonus"
                    :key="`bonus-${slideKey(slide)}`"
                    class="w-full"
                    v-bind="{ gameId }"
                    :keno-bonus="slide.bonus"
                    :is-quick-reveal="false"
                    :multiplier-mode="bonusMode(slide)"
                  />
                </div>
              </LcCard>
            </div>
          </template>
        </LcCarousel>
      </div>

      <p class="paragraph-bold mb-050">{{ $t('highfrequencygame.hub.results.search.label') }}</p>

      <LcForm
        ref="searchForm"
        :validation-schema="validationSchema"
        :tracking="{ containerId: 'high_frequency_game_results_search_form' }"
        @submit="onSubmit"
      >
        <LcFormField v-slot="{ field }" name="gameNumber" mode="blur">
          <LcInput
            v-bind="field"
            :aria-label="$t('highfrequencygame.hub.results.search.input.label')"
            inputmode="numeric"
            maxlength="7"
            :label="
              field.value || searchFocused
                ? $t('highfrequencygame.hub.results.search.input.label')
                : ''
            "
            :placeholder="
              searchFocused
                ? $t('highfrequencygame.hub.results.search.input.example', {
                    gameNumber: headerGameNumber,
                  })
                : $t('highfrequencygame.hub.results.search.input.placeholder')
            "
            @focus="searchFocused = true"
            @blur="searchFocused = false"
          >
            <template #suffix>
              <LcButton
                v-if="field.value"
                :aria-label="$t('highfrequencygame.hub.results.search.clear.arialabel')"
                icon="close"
                size="small"
                type="button"
                variant="tertiary"
                @click.prevent="(searchFormRef?.internal?.resetField('gameNumber'), emit('search'))"
              />
              <div v-if="field.value" class="search-divider" />
              <LcButton
                :aria-label="$t('highfrequencygame.hub.results.search.input.placeholder')"
                icon="search"
                size="small"
                type="submit"
                variant="tertiary"
              />
            </template>
          </LcInput>
        </LcFormField>
      </LcForm>
    </LcCard>

    <LcCard class="bottom-content w-full mt-200" body-class="p-300" shadow="none">
      <p class="headline paragraph-bold mb-200">
        {{ $t('highfrequencygame.hub.results.playnow.headline') }}
      </p>
      <LcButton class="w-full" color="primary" size="medium" @click="emit('play-now')">
        {{ $t('highfrequencygame.hub.results.playnow.button') }}
      </LcButton>
    </LcCard>
  </section>
</template>

<script lang="ts" setup>
import type { LcCarousel, LcCard, LcHighFrequencyBallConfig } from '@lottocom/frontend-components'
import type { ComponentExposed } from 'vue-component-type-helpers'

import { HighFrequencyGameId } from '@lottocom/high-frequency-game-mapping'
import * as Yup from 'yup'

import LcForm from '#tracking/app/component-overrides/LcForm.vue'
import LcFormField from '#tracking/app/component-overrides/LcFormField.vue'

import type { KenoDrawSlide } from '../composables/kenoHub'

const BALL_STAGGER_MS = 15
const BALL_ANIMATION_MS = 250
const ACTIVATION_DELAY_MS = 200

const BASE_BALL_CONFIG: LcHighFrequencyBallConfig[] = [
  { left: '7%', opacity: 0.4, size: 'medium', top: '25%' },
  { left: '95%', opacity: 0.4, size: 'medium', top: '45%' },
  { left: '75%', opacity: 0.4, size: 'medium', top: '5%' },
  { left: '10%', opacity: 0.4, size: 'medium', top: '70%' },
  { left: '90%', opacity: 0.4, size: 'medium', top: '80%' },
  { left: '30%', opacity: 0.4, size: 'medium', top: '95%' },
]

type HighFrequencyGameResultsCardProps = {
  canNavigateNewer?: boolean
  canNavigateOlder?: boolean
  currentDrawNumber?: string
  draws?: KenoDrawSlide[]
  gameId: HighFrequencyGameId
  hasNewGame?: boolean
  isGameNotFound?: boolean
  isLatest?: boolean
  isSearching?: boolean
}

type HighFrequencyGameResultsCardEmits = {
  (e: 'new-game' | 'play-now'): void
  (e: 'change', drawNumber: string): void
  (e: 'search', gameNumber?: Maybe<string>): void
}

const indexOfDraw = (draws: KenoDrawSlide[], drawNumber?: string) => {
  const lastIndex = draws.length ? draws.length - 1 : 0
  if (!drawNumber) return lastIndex

  const index = draws.findIndex((draw) => draw.drawNumber === drawNumber)
  return index === -1 ? lastIndex : index
}

const {
  canNavigateNewer = false,
  canNavigateOlder = false,
  currentDrawNumber,
  draws = [],
  gameId,
  hasNewGame = false,
  isGameNotFound = false,
  isLatest = false,
  isSearching = false,
} = defineProps<HighFrequencyGameResultsCardProps>()

const emit = defineEmits<HighFrequencyGameResultsCardEmits>()

const { t } = useI18n()
const licensedTerritory = useLicensedTerritory()

const searchFormRef = useTemplateRef<ComponentExposed<typeof LcForm>>('searchForm')
const carouselRef = useTemplateRef<ComponentExposed<typeof LcCarousel>>('carouselRef')
const resultsViewport = useTemplateRef<HTMLElement>('resultsViewport')
const searchFocused = ref(false)
const isMounted = ref(false)
const activeSlideIndex = ref(indexOfDraw(draws, currentDrawNumber))
const activatedDrawNumber = ref<string | undefined>(draws[activeSlideIndex.value]?.drawNumber)
const pendingDrawNumber = ref(activatedDrawNumber.value)
const revealCount = ref(0)
const isCarouselReady = ref(false)
const hasRevealedInitialSlide = ref(false)
const isResultsVisible = useElementVisibility(resultsViewport)
const backgroundBallConfig = useState(() =>
  BASE_BALL_CONFIG.map((option) => ({
    ...option,
    content: Math.floor(Math.random() * 50 + 10).toString(),
  })),
)
let activationTimer: ReturnType<typeof setTimeout> | undefined
const viewportHeight = useState<number | undefined>('hfg-results-viewport-height', () => undefined)

const innerCardStyle = computed(() =>
  viewportHeight.value ? { minHeight: `${viewportHeight.value}px` } : undefined,
)

let resizeObserver: ResizeObserver | undefined

watchImmediate(resultsViewport, (el, _, onCleanup) => {
  if (getIsServer() || !el) return

  resizeObserver?.disconnect()

  resizeObserver = new ResizeObserver(([entry]) => {
    const height = entry?.contentRect.height
    if (height && height > (viewportHeight.value ?? 0)) {
      viewportHeight.value = height
    }
  })
  resizeObserver.observe(el)

  onCleanup(() => resizeObserver?.disconnect())
})

const currentDrawIndex = computed(() => indexOfDraw(draws, currentDrawNumber))

const carouselSlides = computed(() => {
  if (isMounted.value) return draws
  const currentDraw = draws[currentDrawIndex.value]
  return currentDraw ? [currentDraw] : []
})
const carouselStartIndex = computed(() => (isMounted.value ? currentDrawIndex.value : 0))

const isInitialReveal = computed(() => revealCount.value === 0)

const headerGameNumber = computed(() => currentDrawNumber || draws.at(-1)?.drawNumber)

const theme = computed(() => {
  const { createCssVar } = getLotteryCssVarConstructor(licensedTerritory, gameId)

  return {
    '--card-multiplier-color': createCssVar('high-frequency-bonus-game-card-text'),
    '--high-frequency-ball-background': createCssVar('high-frequency-ball-background'),
    '--high-frequency-ball-color': createCssVar('high-frequency-ball-text-color'),
    '--high-frequency-game-background-color': createCssVar(
      'high-frequency-results-background-color',
    ),
    '--high-frequency-game-results-bottom-gradient': createCssVar(
      'high-frequency-results-bottom-gradient',
    ),
    '--high-frequency-game-results-bottom-text': createCssVar('high-frequency-results-bottom-text'),
  }
})

const validationSchema = Yup.object().shape({
  gameNumber: Yup.string()
    .matches(/^\d*$/, t('highfrequencygame.hub.results.search.input.error'))
    .test(
      'length',
      t('highfrequencygame.hub.results.search.input.error'),
      (v) => !v || v.length === 7,
    ),
})

const isSlideActivated = (slide: KenoDrawSlide) => activatedDrawNumber.value === slide.drawNumber

const slideKey = (slide: KenoDrawSlide) =>
  isSlideActivated(slide) ? `reveal-${revealCount.value}` : `idle-${slide.drawNumber}`

const bonusMode = (slide: KenoDrawSlide): 'enter' | 'hidden' | undefined => {
  if (!isSlideActivated(slide)) return 'hidden'
  return isInitialReveal.value ? undefined : 'enter'
}

const activateSlide = (drawNumber: string) => {
  pendingDrawNumber.value = drawNumber
  if (activatedDrawNumber.value === drawNumber) return
  activatedDrawNumber.value = drawNumber
  revealCount.value++
}

const scheduleActivation = (drawNumber: string) => {
  if (activationTimer) clearTimeout(activationTimer)
  pendingDrawNumber.value = drawNumber
  activatedDrawNumber.value = undefined
  activationTimer = setTimeout(() => activateSlide(drawNumber), ACTIVATION_DELAY_MS)
}

const activateActiveSlide = () => {
  const activeSlide = draws[activeSlideIndex.value]
  if (activeSlide) activateSlide(activeSlide.drawNumber)
}

const onCarouselReady = () => {
  activeSlideIndex.value = currentDrawIndex.value
  isCarouselReady.value = true
  if (hasRevealedInitialSlide.value) activateActiveSlide()
}

const onCarouselChanged = (index: number) => {
  activeSlideIndex.value = index
  const slide = draws[index]
  if (!slide) return
  scheduleActivation(slide.drawNumber)
  emit('change', slide.drawNumber)
}

const fieldsForSlide = (slide: KenoDrawSlide) => {
  if (!slide.drawnNumbers.length) return []
  return [
    { name: 'main', numbers: slide.drawnNumbers.map((value) => ({ matching: false, value })) },
  ]
}

const onSubmit = ({ gameNumber }: { gameNumber?: string }) => {
  emit('search', gameNumber)
}

watchImmediate([isResultsVisible, isCarouselReady], ([visible, ready]) => {
  if (!visible || !ready || hasRevealedInitialSlide.value) return
  hasRevealedInitialSlide.value = true
  activateActiveSlide()
})

watch([() => draws, () => currentDrawNumber], async (_, [previousDraws]) => {
  const targetIndex = indexOfDraw(draws, currentDrawNumber)
  const targetDrawNumber = draws[targetIndex]?.drawNumber

  await nextTick()
  if (!carouselRef.value) return

  if (targetIndex !== activeSlideIndex.value) {
    const activeSlideDrawNumber = previousDraws?.[activeSlideIndex.value]?.drawNumber
    const isReindexOfCurrentDraw =
      activeSlideDrawNumber !== undefined &&
      activeSlideDrawNumber === currentDrawNumber &&
      targetDrawNumber === currentDrawNumber

    carouselRef.value.moveToIndex(targetIndex, !isReindexOfCurrentDraw)
    return
  }

  if (targetDrawNumber && pendingDrawNumber.value !== targetDrawNumber) {
    scheduleActivation(targetDrawNumber)
  }
})

onMounted(() => {
  isMounted.value = true
})

onBeforeUnmount(() => {
  if (activationTimer) clearTimeout(activationTimer)
})
</script>

<style lang="scss" scoped>
.high-frequency-results-card {
  position: relative;

  .background {
    position: absolute;
    inset: 0;
    z-index: 0;

    /* stylelint-disable-next-line selector-class-pattern */
    :deep(.lc-high-frequency-ball-background__gradient) {
      background: none;
    }
  }

  .inner-card-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .results-carousel {
    margin-inline: calc(-1 * #{token('size/space/gap/100')});

    /* stylelint-disable-next-line selector-class-pattern */
    :deep(.lc-carousel__container) {
      overflow: clip visible;
      overflow-clip-margin: token('size/space/gap/100');
    }

    /* stylelint-disable-next-line selector-class-pattern */
    :deep(.lc-carousel__wrap) {
      margin-top: 0;
    }
  }

  .slide {
    flex: 0 0 100%;
    min-width: 0;
    padding-inline: token('size/space/gap/100');

    .animated-numbers {
      padding-right: 0.125rem;
    }

    &:not(.activated) {
      /* stylelint-disable-next-line selector-class-pattern */
      :deep(.number-slot-text) {
        visibility: hidden;
      }
    }
  }

  .new-game-badge {
    position: absolute;
    top: -1.2rem;
    left: 50%;
    z-index: 10;
    display: flex;
    gap: token('size/space/gap/050');
    align-items: center;
    padding: token('size/space/gap/050') token('size/space/gap/100');
    border: none;
    box-shadow: token('shadow/shadow/card/extrasmall');
    font-family: inherit;
    white-space: nowrap;
    cursor: pointer;
    transform: translateX(-50%);

    .new-game-badge-icon {
      animation: badge-icon-pulse 0.9s ease infinite;
    }
  }

  .fade-game-number-enter-active {
    transition: opacity 0.15s ease-in-out;
  }

  .fade-game-number-enter-from {
    opacity: 0;
  }

  .latest-badge {
    opacity: 0;

    &.visible {
      opacity: 1;
      transition: opacity 0.15s ease-in-out;
    }
  }

  @keyframes badge-icon-pulse {
    0%,
    100% {
      transform: translateX(0);
    }

    50% {
      transform: translateX(3px);
    }
  }

  .game-not-found-description {
    max-width: 29rem;
  }

  .game-not-found-illustration {
    flex-shrink: 0;
    width: 96px;
  }

  .search-divider {
    width: 1px;
    height: token('size/space/gap/200');
    margin-inline: token('size/space/gap/100');
    background-color: token('color/border/primary/primary');
  }

  .bottom-content {
    background: var(
      --high-frequency-game-results-bottom-gradient,
      linear-gradient(90deg, rgb(32, 65, 175, 80%) 0%, rgb(15, 19, 60, 80%) 100%)
    );

    .headline {
      color: var(--high-frequency-game-results-bottom-text, token('color/text/primary/primary'));
    }
  }
}
</style>
